import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import { showErrorToast } from '../utils/toast';
import { formatBytesAsSize, triggerDownload } from '../utils/imageUtils';
import { StatusPill } from './shared/StatusPill';
import OptimizationTypeSelector from './shared/OptimizationTypeSelector';
import OptimizedImagesManagement from './OptimizedImagesManagement';
import CompareModal from './shared/CompareModal';
import CreditsWidget from './shared/CreditsWidget';
import useManualUpload from '../hooks/useManualUpload';
import useFileDropZone, { ACCEPTED_LABEL } from '../hooks/useFileDropZone';
import useOptimizationType from '../hooks/useOptimizationType';
import PaginationFooter from './shared/PaginationFooter';
import ProductSelection from './ProductSelection';

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, tone, isLoading }) {
  return (
    <s-box background="base" border="base" borderRadius="base" padding="base">
      <s-stack gap="small">
        <s-text tone="subdued" size="small">{label}</s-text>
        {isLoading
          ? <div className="shimmer-loader shimmer-text-medium" />
          : <s-text type="strong" tone={tone}>{value}</s-text>
        }
      </s-stack>
    </s-box>
  );
}

// ─── Summary Stats Bar ────────────────────────────────────────────────────────

function SummaryStatsBar({ stats, isLoading }) {
  const products = stats?.products || {};
  const savings  = stats?.savings  || {};
  const lifetime = stats?.lifetime || {};
  return (
    <s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
      <StatCard label="Store Products"        value={products.total ?? 0} isLoading={isLoading} />
      <StatCard label="Store Product Images"  value={lifetime.storeImages ?? 0} tone={(lifetime.storeImages ?? 0) > 0 ? 'success' : undefined} isLoading={isLoading} />
      <StatCard label="Manual Upload Images"  value={lifetime.manualUploads ?? 0} tone={(lifetime.manualUploads ?? 0) > 0 ? 'success' : undefined} isLoading={isLoading} />
      <StatCard label="Lifetime Saved"        value={`${(savings.totalSavedMb ?? 0).toFixed(2)} MB`} tone={(savings.totalSavedMb ?? 0) > 0 ? 'success' : undefined} isLoading={isLoading} />
    </s-grid>
  );
}

// ─── Upload Stats Row ─────────────────────────────────────────────────────────

function UploadStatsRow({ sessionData }) {
  const done = (sessionData?.files || []).filter(f => f.status === 'done');
  const queued = (sessionData?.queued ?? 0) + (sessionData?.processing ?? 0);
  const completed = sessionData?.completed ?? 0;
  let totalSaved = 0, avgReduction = 0;
  if (done.length > 0) {
    const origKb  = done.reduce((s, f) => s + (f.originalSizeKb ?? 0), 0);
    const optKb   = done.reduce((s, f) => s + (f.optimizedSizeKb ?? 0), 0);
    totalSaved    = Math.max(0, origKb - optKb) * 1024;
    const pcts    = done.map(f => Number(f.savingsPercent) || 0);
    avgReduction  = pcts.reduce((s, p) => s + p, 0) / pcts.length;
  }
  return (
    <s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
      <StatCard label="In Queue"      value={queued} />
      <StatCard label="Total Saved"   value={formatBytesAsSize(totalSaved)}  tone={totalSaved    > 0 ? 'success' : undefined} />
      <StatCard label="Avg Reduction" value={avgReduction > 0 ? `${avgReduction.toFixed(2)}%` : '—'} tone={avgReduction > 0 ? 'success' : undefined} />
      <StatCard label="Completed"     value={completed} />
    </s-grid>
  );
}

// ─── Batch file table column template ─────────────────────────────────────────

/** Thumb | File | Original | Compressed | Saving | Status | Actions */
const FILE_COLS = '44px minmax(120px,1fr) 72px 88px 72px 108px 210px';
const STAGED_FILE_COLS = '44px minmax(120px,1fr) 72px 88px 72px 108px 80px';

function normalizeRowState(file) {
  // Live sessions use `liveStatus` with values we control: pending/processing/done/failed/cancelled
  if (file.liveStatus) return file.liveStatus;

  // Recents sessions use `optimizationStatus` from DB: optimized/failed/skipped (and sometimes cancelled)
  const o = file.optimizationStatus;
  if (o === 'optimized') return 'done';
  if (o === 'failed') return 'failed';
  if (o === 'cancelled') return 'cancelled';
  if (o === 'skipped') return 'skipped';
  if (o === 'processing') return 'processing';
  if (o === 'pending') return 'pending';

  // Fallback
  return 'pending';
}

function SessionSummaryFooter({ totalFiles, totalSavedKb, savingsPct }) {
  return (
    <div className="mu-session-summary-row">
      <div className="mu-session-summary-card">
        <s-text size="small" tone="subdued">Total files</s-text>
        <s-text type="strong">{totalFiles}</s-text>
      </div>
      <div className="mu-session-summary-card">
        <s-text size="small" tone="subdued">Total saved</s-text>
        <s-text type="strong">{totalSavedKb > 0 ? formatBytesAsSize(totalSavedKb * 1024) : '—'}</s-text>
      </div>
      <div className="mu-session-summary-card">
        <s-text size="small" tone="subdued">Savings</s-text>
        <s-text type="strong" tone={Number(savingsPct) > 0 ? 'success' : undefined}>
          {Number(savingsPct) > 0 ? `${Number(savingsPct).toFixed(1)}%` : '—'}
        </s-text>
      </div>
    </div>
  );
}

// ─── LazyImg ──────────────────────────────────────────────────────────────────
// Fetches the compare/optimized URL once; uses stable fetchRef so it doesn't
// trigger re-renders when authenticatedFetch identity changes.

function LazyImg({ file, sessionId, fetchRef }) {
  const [src, setSrc] = useState(
    file.inlineOptimizedUrl || file.shopifyOptimizedFileUrl || file.shopifyOriginalFileUrl || file.localPreviewUrl || null
  );

  // Upgrade src when the file finishes optimizing and inlineOptimizedUrl becomes available
  useEffect(() => {
    if (file.inlineOptimizedUrl) setSrc(file.inlineOptimizedUrl);
  }, [file.inlineOptimizedUrl]);

  // Fetch compare URL for past sessions that have neither inline nor CDN URL yet
  useEffect(() => {
    if (!file.hasCompare || !file.safeName) return;
    // Skip if we already have a real optimized URL (not just a local blob preview)
    if (file.inlineOptimizedUrl || file.shopifyOptimizedFileUrl) return;
    let cancelled = false;
    fetchRef.current(`/api/manual-upload/compare/${sessionId}/${encodeURIComponent(file.safeName)}`)
      .then(r => r.json())
      .then(d => { if (!cancelled && d.success && d.optimizedUrl) setSrc(d.optimizedUrl); })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — only needed for past sessions without URLs

  if (src) {
    return (
      <div className="batch-thumb-cell">
        <img src={src} alt="" className="batch-file-thumbnail" />
      </div>
    );
  }
  return (
    <div className="batch-thumb-cell">
      <span className="batch-file-thumbnail-placeholder">🖼️</span>
    </div>
  );
}

// ─── BatchFileRow ─────────────────────────────────────────────────────────────

function BatchFileRow({ file, sessionId, onDownload, onCompare, downloading, fetchRef }) {
  const rowState   = normalizeRowState(file);
  const isDone     = rowState === 'done' || file.optimizationStatus === 'optimized';
  const savingsVal = file.savingsPercent != null ? Number(file.savingsPercent) : null;
  const origLabel  = file.originalSizeKb ? formatBytesAsSize(file.originalSizeKb * 1024) : '—';

  const PILL_STATUSES = ['done', 'processing', 'pending', 'skipped', 'cancelled', 'failed'];
  const statusNode = PILL_STATUSES.includes(rowState)
    ? <StatusPill status={isDone ? 'done' : rowState} />
    : <s-text size="small" tone="subdued">—</s-text>;

  return (
    <div className="batch-table-row" style={{ gridTemplateColumns: FILE_COLS }}>
      <LazyImg file={file} sessionId={sessionId} fetchRef={fetchRef} />

      <div className="batch-file-name-wrap">
        <s-text size="small" type="strong" className="batch-file-name">{file.imageName}</s-text>
      </div>

      <s-text size="small" tone="subdued">
        {origLabel}
      </s-text>

      <div>
        {isDone && file.optimizedSizeKb ? (
          <s-text size="small" tone="success">{formatBytesAsSize(file.optimizedSizeKb * 1024)}</s-text>
        ) : (
          <s-text size="small" tone="subdued">—</s-text>
        )}
      </div>

      <div>
        {isDone && savingsVal != null && savingsVal > 0 ? (
          <s-text size="small" tone="success">{savingsVal.toFixed(0)}%</s-text>
        ) : (
          <s-text size="small" tone="subdued">—</s-text>
        )}
      </div>

      <div>{statusNode}</div>

      <div className="batch-actions">
        {file.shopifyFilesExist === false && file.optimizationStatus === 'optimized' && (
          <s-badge tone="critical" size="small">Removed from Shopify</s-badge>
        )}
        {file.shopifyFilesExist !== false && file.hasCompare && (
          <s-button size="small" variant="secondary" icon="view" className="batch-action-btn" onClick={(e) => { e.stopPropagation(); onCompare(file); }}>
            Compare
          </s-button>
        )}
        {file.shopifyFilesExist !== false && file.hasDownload && (
          <s-button
            size="small"
            variant="secondary"
            icon="download"
            className="batch-action-btn"
            loading={downloading.file === file.safeName || undefined}
            onClick={(e) => { e.stopPropagation(); onDownload(file); }}
          >
            Download
          </s-button>
        )}
      </div>
    </div>
  );
}

// ─── BatchCard (past/recent session accordion) ────────────────────────────────

function BatchCard({ session, onCompare, authenticatedFetch }) {
  const [open, setOpen]         = useState(false);
  const [downloading, setDl]    = useState({ file: null, zip: false });
  // Use a ref so download handlers don't close over a stale authenticatedFetch
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const doneCount   = session.completedFiles ?? 0;
  const total       = session.totalFiles     ?? 0;
  const failed      = session.failedFiles    ?? 0;
  const skipped     = session.skippedFiles   ?? 0;
  const batchStatus =
    failed === total && total > 0             ? 'failed'     :
    (doneCount + failed) >= total && doneCount > 0 ? 'done' : 'processing';

  const dateStr = session.createdAt
    ? new Date(session.createdAt).toLocaleString('en-US', { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' })
    : 'Unknown date';

  const optimizedFiles    = (session.files || []).filter(f => f.optimizationStatus === 'optimized');
  const totalOriginalKb   = optimizedFiles.reduce((s, f) => s + (f.originalSizeKb  || 0), 0);
  const totalOptimizedKb  = optimizedFiles.reduce((s, f) => s + (f.optimizedSizeKb || 0), 0);
  const totalSavedKb      = Math.max(0, totalOriginalKb - totalOptimizedKb);

  const handleDownloadFile = async (file) => {
    if (downloading.file) return;
    setDl(p => ({ ...p, file: file.safeName }));
    try {
      const res = await fetchRef.current(`/api/manual-upload/download/${session.sessionId}/${encodeURIComponent(file.safeName)}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.message || 'Download failed');
        return;
      }

      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/zip')) {
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        triggerDownload(blobUrl, `${file.safeName}_optimized.${file.ext}`);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const data = await res.json();
        if (data.success && data.url) triggerDownload(data.url, `${file.safeName}_optimized.${file.ext}`);
        else showErrorToast(data.message || 'Download failed');
      }
    } catch (e) { showErrorToast(e.message || 'Download failed'); }
    finally { setDl(p => ({ ...p, file: null })); }
  };

  const handleDownloadZip = async (e) => {
    e.stopPropagation();
    if (downloading.zip) return;
    setDl(p => ({ ...p, zip: true }));
    try {
      const res = await fetchRef.current(`/api/manual-upload/download-zip/${session.sessionId}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.message || 'ZIP download failed');
        return;
      }

      const contentType = res.headers.get('Content-Type');
      if (contentType && contentType.includes('application/zip')) {
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        triggerDownload(blobUrl, 'optimized_images.zip');
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const data = await res.json();
        if (data.success && data.url) triggerDownload(data.url, data.filename || 'optimized_images.zip');
        else showErrorToast(data.message || 'ZIP download failed');
      }
    } catch (e) { showErrorToast(e.message || 'ZIP download failed'); }
    finally { setDl(p => ({ ...p, zip: false })); }
  };

  const handleCompare = async (file) => {
    try {
      const res  = await fetchRef.current(`/api/manual-upload/compare/${session.sessionId}/${encodeURIComponent(file.safeName)}`);
      const data = await res.json();
      if (data.success) {
        onCompare({ originalUrl: data.originalUrl, optimizedUrl: data.optimizedUrl,
          originalName: file.imageName, originalSizeKb: file.originalSizeKb,
          optimizedSizeKb: file.optimizedSizeKb, savingsPercent: file.savingsPercent });
      } else showErrorToast(data.message || 'Compare failed');
    } catch (e) { showErrorToast(e.message || 'Compare failed'); }
  };

  const statusTone = batchStatus === 'done' ? 'success' : batchStatus === 'failed' ? 'critical' : 'info';

  return (
    <div className={`batch-card batch-card-${batchStatus === 'done' ? 'success' : batchStatus === 'failed' ? 'critical' : 'info'}`}>
      {/* Header */}
      <div className="batch-card-header" onClick={() => setOpen(o => !o)}>
        <s-stack direction="inline" gap="small" alignItems="center" className="batch-card-header-left">
          <s-button
            variant="tertiary"
            icon={open ? 'chevron-down' : 'chevron-right'}
            onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          />
          <s-stack direction="inline" gap="small" alignItems="center" wrap>
            <s-text type="strong" size="small">{dateStr}</s-text>
            <s-badge tone={statusTone} size="small">{doneCount}/{total} done</s-badge>
            {failed > 0 && <s-badge tone="critical" size="small">{failed} failed</s-badge>}
            {skipped > 0 && <s-badge tone="attention" size="small">{skipped} skipped</s-badge>}
            {session.optimizationType && <s-badge size="small">{session.optimizationType}</s-badge>}
            {totalOriginalKb > 0 && (
              <s-badge size="small">
                {formatBytesAsSize(totalOriginalKb * 1024)} → {formatBytesAsSize(totalOptimizedKb * 1024)}
              </s-badge>
            )}
            {totalSavedKb > 0 && (
              <s-badge tone="success" size="small">{formatBytesAsSize(totalSavedKb * 1024)} saved</s-badge>
            )}
          </s-stack>
        </s-stack>

        <div onClick={e => e.stopPropagation()}>
          {doneCount > 1 && (
            <s-button size="small" variant="secondary" icon="download" disabled={downloading.zip} onClick={handleDownloadZip}>
              {downloading.zip ? 'Preparing…' : 'Download All'}
            </s-button>
          )}
        </div>
      </div>

      {/* File table (always mounted so thumbnails load before accordion open).
          We only collapse the visuals via max-height/opacity. */}
      <div
        className="batch-card-body"
        style={{
          maxHeight: open ? 2400 : 0,
          overflow: 'hidden',
          opacity: open ? 1 : 0,
          transition: 'max-height 0.25s ease, opacity 0.15s ease',
        }}
        aria-hidden={!open}
      >
        <s-box padding="base">
          <s-stack gap="base">
            {(session.files || []).length > 0 && doneCount > 0 && totalOriginalKb > 0 && (
              <SessionSummaryFooter
                totalFiles={session.files?.length ?? total}
                totalSavedKb={totalSavedKb}
                savingsPct={((totalSavedKb / totalOriginalKb) * 100).toFixed(1)}
              />
            )}

            <div className="batch-table-header" style={{ gridTemplateColumns: FILE_COLS }}>
              <span />
              <s-text size="small" type="strong">File</s-text>
              <s-text size="small" type="strong">Original</s-text>
              <s-text size="small" type="strong">Compressed</s-text>
              <s-text size="small" type="strong">Saving</s-text>
              <s-text size="small" type="strong">Status</s-text>
              <s-text size="small" type="strong" style={{ textAlign: 'right', display: 'block' }}>Actions</s-text>
            </div>
            {(session.files || []).map((file, idx) => (
                <BatchFileRow
                  key={file.safeName || file.id || idx}
                  file={file}
                  sessionId={session.sessionId}
                  onDownload={handleDownloadFile}
                  onCompare={handleCompare}
                  downloading={downloading}
                  fetchRef={fetchRef}
                />
              ))}
          </s-stack>
        </s-box>
      </div>
    </div>
  );
}

// ─── RecentUploads ────────────────────────────────────────────────────────────
// Uses a ref for authenticatedFetch so that `load` never needs to be in the
// useCallback dep array — prevents infinite re-render loops.

function RecentUploads({ recentsKey, onCompare, currentSessionId }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  // Stable ref — never changes identity, so useCallback deps stay empty
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [pageInfo, setPageInfo] = useState(null);

  // Using arguments so load remains stable
  const load = useCallback(async (p, excludeId) => {
    setLoading(true);
    try {
      const url = excludeId
        ? `/api/manual-upload/recents?page=${p}&pageSize=5&excludeSessionId=${excludeId}`
        : `/api/manual-upload/recents?page=${p}&pageSize=5`;
      const res  = await fetchRef.current(url);
      const data = await res.json();
      if (data.success) {
        setSessions(data.sessions || []);
        setPageInfo(data.pageInfo || null);
      }
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, []); // ← empty deps: load is stable

  useEffect(() => { load(page, currentSessionId); }, [recentsKey, page, currentSessionId, load]);

  if (loading && sessions.length === 0) {
    return (
      <s-stack gap="small">
        {[1, 2, 3].map(i => (
          <div key={i} className="shimmer-loader" style={{ height: 52, borderRadius: 8 }} />
        ))}
      </s-stack>
    );
  }

  if (!loading && sessions.length === 0) return null;

  return (
    <s-stack gap="small">
      {/* Header */}
      <s-stack direction="inline" justifyContent="space-between" alignItems="center">
        <s-stack direction="inline" gap="small" alignItems="center">
          <s-text type="strong">Recent Uploads</s-text>
          {pageInfo && (
            <s-badge size="small">{pageInfo.totalSessions} session{pageInfo.totalSessions !== 1 ? 's' : ''}</s-badge>
          )}
        </s-stack>
        <s-button variant="secondary" size="small" onClick={() => load(page, currentSessionId)}>
          Refresh
        </s-button>
      </s-stack>

      {/* Session cards */}
      {loading ? (
        <s-stack gap="small">
          {[1, 2, 3].map(i => (
            <div key={i} className="shimmer-loader" style={{ height: 52, borderRadius: 8 }} />
          ))}
        </s-stack>
      ) : (
        <s-stack gap="small">
          {sessions.map(s => (
            <BatchCard
              key={s.sessionId}
              session={s}
              onCompare={onCompare}
              authenticatedFetch={authenticatedFetch}
            />
          ))}
          {sessions.length === 0 && (
            <s-text tone="subdued" size="small">No other sessions on this page.</s-text>
          )}
        </s-stack>
      )}

      {/* Pagination */}
      {pageInfo && pageInfo.totalPages > 1 && (
        <PaginationFooter
          pageInfo={{ ...pageInfo, totalProducts: pageInfo.totalSessions }}
          onPrevious={() => setPage(p => p - 1)}
          onNext={() => setPage(p => p + 1)}
          loading={loading}
          itemName="sessions"
        />
      )}
    </s-stack>
  );
}

// ─── StagedThumb ──────────────────────────────────────────────────────────────
// Creates a stable object URL per File, revoked on unmount.

function StagedThumb({ file }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!file?.type?.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => { URL.revokeObjectURL(url); };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="batch-thumb-cell">
      {src
        ? <img src={src} alt="" className="batch-file-thumbnail" />
        : <span className="batch-file-thumbnail-placeholder">🖼️</span>
      }
    </div>
  );
}

// ─── ImageCrushPage ───────────────────────────────────────────────────────────

function ImageCrushPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticatedFetch } = useAuthenticatedFetch();
  // Stable ref — prevents fetchStats from being recreated on each render
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [mainTab, setMainTab]   = useState(0);
  const [subTab,  setSubTab]    = useState(0);
  const [stats, setStats]       = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [compareData, setCompareData]   = useState(null);

  // fetchStats reads authenticatedFetch via ref → stable, no deps needed
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await fetchRef.current('/api/stats');
      if (res.ok) setStats(await res.json());
    } catch { /* non-critical */ }
    finally { setStatsLoading(false); }
  }, []); // ← empty deps: fetchStats is stable

  // Handle tab from query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'optimized') {
      setMainTab(0);
      setSubTab(1);
    }
  }, [location.search]);

  // Runs once on mount only, because fetchStats is stable
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const {
    session, downloading, recentsKey, initializing,
    startUpload, handleAbort, handleDownloadFile, handleDownloadZip, handleReset,
  } = useManualUpload();

  // Reference for progress section for auto-scrolling
  const progressRef = useRef(null);

  // Local blob URLs captured before clearAll() so thumbnails show during processing
  const localPreviewsRef = useRef({});

  const cleanupLocalPreviews = useCallback(() => {
    Object.values(localPreviewsRef.current).forEach(url => {
      try { URL.revokeObjectURL(url); } catch {}
    });
    localPreviewsRef.current = {};
  }, []);

  const { phase, data: sessionData, uploadError, aborting } = session;

  // Auto-scroll to progress section when optimization starts
  useEffect(() => {
    if (phase === 'optimizing' && progressRef.current) {
      setTimeout(() => {
        progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500); // Wait for the card to render
    }
  }, [phase]);

  // Handle navigateToPricing event from useManualUpload
  useEffect(() => {
    const handleNavToPricing = () => navigate('/pricing');
    window.addEventListener('navigateToPricing', handleNavToPricing);
    return () => window.removeEventListener('navigateToPricing', handleNavToPricing);
  }, [navigate]);

  // Fetches the shop's default compression level from DB — used for the upload type selector
  const { optimizationType: uploadOptType, handleOptimizationTypeChange: setUploadOptType } = useOptimizationType('regular');

  const {
    files, errors, isDragOver,
    clearAll, reset: resetFiles, removeFile,
    handleDragOver, handleDragLeave, handleDrop, handleFileInputChange,
  } = useFileDropZone();

  const fileInputRef = useRef(null);

  // NOTE: Intentionally not resetting on visibilitychange — tab becoming hidden
  // (e.g. due to a download dialog) must not wipe the active session.
  // An active (optimizing) session persists across tab/page changes.
  // A completed/aborted session is cleared when the user leaves (it's visible in Recents).

  // Use a ref so cleanup closures always read the latest phase
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // When switching to the Store Products tab after a completed session, reset so
  // coming back to Manual Upload shows the drop zone (not a stale done state).
  const prevMainTabRef = useRef(mainTab);
  useEffect(() => {
    if (prevMainTabRef.current === 1 && mainTab !== 1 && phaseRef.current === 'done') {
      handleReset();
      resetFiles();
    }
    prevMainTabRef.current = mainTab;
  }, [mainTab, handleReset, resetFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // When navigating away after a completed session, reset so returning shows
  // the drop zone — the completed session is already visible in Recents.
  useEffect(() => {
    return () => {
      if (phaseRef.current === 'done') { handleReset(); resetFiles(); }
    };
  }, [location.pathname, handleReset, resetFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    if (!files.length || phase !== 'idle') return;
    // Capture local blob URLs before clearAll() so thumbnails remain visible during processing
    cleanupLocalPreviews();
    const previews = {};
    files.forEach(f => {
      if (f.type?.startsWith('image/')) {
        try { previews[f.name] = URL.createObjectURL(f); } catch {}
      }
    });
    localPreviewsRef.current = previews;
    await startUpload(files, uploadOptType);
    clearAll();
  };

  const totalSelectedSize = files.reduce((s, f) => s + (f.size || 0), 0);

  // ── Build file rows from live session ─────────────────────────────────────
  const fileRows = (() => {
    if (phase === 'idle') return [];
    return (sessionData?.files || []).map((f, i) => ({
      id: `s-${i}-${f.originalName}`,
      imageName: f.originalName || f.safeName || `file-${i}`,
      originalSizeKb:  f.originalSizeKb  ?? null,
      optimizedSizeKb: f.optimizedSizeKb ?? null,
      savingsPercent:  f.savingsPercent   ? Number(f.savingsPercent) : null,
      optimizationStatus:
        f.status === 'processing' ? 'processing' :
        f.status === 'queued' ? 'pending' :
        f.status === 'done'       ? 'optimized'  :
        f.status === 'failed'     ? 'failed'      :
        f.status === 'cancelled'  ? 'cancelled'   :
        f.status === 'skipped'    ? 'skipped'     : 'pending',
      liveStatus: f.status === 'processing' ? 'processing'
               : f.status === 'queued'     ? 'pending'
               : f.status === 'done'       ? 'done'
               : f.status === 'failed'     ? 'failed'
               : f.status === 'cancelled' ? 'cancelled'
               : f.status === 'skipped'   ? 'skipped'
               : 'pending',
      hasDownload: f.status === 'done',
      // Live polling response uses `originalUrl/optimizedUrl`
      hasCompare:  f.status === 'done' && !!f.originalUrl && !!f.optimizedUrl,
      inlineOptimizedUrl: f.optimizedUrl || null,
      // Local blob URL captured at upload time — shows thumbnail before optimization completes
      localPreviewUrl: localPreviewsRef.current[f.originalName] || null,
      safeName: f.safeName || '',
      ext: f.ext || 'jpg',
      shopifyFilesExist: f.shopifyFilesExist !== false,
    }));
  })();

  // Shared fetchRef passed to BatchFileRow → avoids prop-drilling authenticatedFetch
  const liveFetchRef = useRef(authenticatedFetch);
  useEffect(() => { liveFetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  // ── Live session download/compare handlers ────────────────────────────────
  const handleLiveDownload = async (file) => {
    const liveRow = sessionData?.files?.find(f => f.safeName === file.safeName);
    if (liveRow?.optimizedUrl) {
      // Delegate to hook handler (passes the raw session file)
      handleDownloadFile(liveRow);
      return;
    }
    showErrorToast('File not ready for download yet.');
  };

  const handleLiveCompare = (file) => {
    const liveRow = sessionData?.files?.find(f => f.safeName === file.safeName);
    if (liveRow?.originalUrl && liveRow?.optimizedUrl) {
      setCompareData({
        originalUrl:     liveRow.originalUrl,
        optimizedUrl:    liveRow.optimizedUrl,
        originalName:    file.imageName,
        originalSizeKb:  file.originalSizeKb,
        optimizedSizeKb: file.optimizedSizeKb,
        savingsPercent:  file.savingsPercent,
      });
    }
  };

  // ── Compute live session progress numbers ─────────────────────────────────
  const liveDone       = fileRows.filter(r => r.liveStatus === 'done').length;
  const liveFailed     = fileRows.filter(r => r.liveStatus === 'failed').length;
  const liveTotal      = fileRows.length;
  const liveProgressPct = liveTotal > 0 ? Math.round(((liveDone + liveFailed) / liveTotal) * 100) : 0;

  const doneFiles = sessionData?.files?.filter(f => f.status === 'done') ?? [];
  const totalOriginalKb  = doneFiles.reduce((s, f) => s + (f.originalSizeKb  ?? 0), 0);
  const totalOptimizedKb = doneFiles.reduce((s, f) => s + (f.optimizedSizeKb ?? 0), 0);
  const totalSavedKb     = Math.max(0, totalOriginalKb - totalOptimizedKb);

  const unoptimizedTabCount = stats?.products
    ? (stats.products.unoptimizedTabCount ?? ((stats.products.unoptimized ?? 0) + (stats.products.partiallyOptimized ?? 0)))
    : 0;
  const optimizedTabCount = stats?.products
    ? (stats.products.optimizedTabCount ?? (stats.products.optimized ?? 0))
    : 0;

  return (
    <s-section>
      <s-stack gap="base">

        {/* Page header with credits widget pinned top-right */}
        <s-stack direction="inline" justifyContent="space-between" alignItems="start">
          <s-stack gap="small">
            <s-heading>Product & Manual Crush</s-heading>
            <s-text tone="subdued">Upload and optimise images manually, or crush your shopify product images in bulk</s-text>
          </s-stack>
          <div className="credits-sticky-top">
            <CreditsWidget onPurchase={() => navigate('/pricing')} />
          </div>
        </s-stack>

        {/* ── Main tab bar ── */}
        <div className="tab-bar-container">
          <div className="tab-container-flex">
            <button
              className={`tab-button ${mainTab === 0 ? 'tab-button-active' : ''}`}
              onClick={() => setMainTab(0)}
            >
              Store Products
              {!statsLoading && stats?.products && (
                <s-badge size="small" tone={(stats.products.total ?? 0) > 0 ? 'info' : 'neutral'}>
                  {stats.products.total ?? 0}
                </s-badge>
              )}
            </button>
            <button
              className={`tab-button ${mainTab === 1 ? 'tab-button-active' : ''}`}
              onClick={() => setMainTab(1)}
            >
              Manual Upload
            </button>
          </div>
          {mainTab === 1 && phase === 'done' && (
            <div className="tab-bar-action">
              <s-button variant="primary" size="small" onClick={() => { handleReset(); resetFiles(); }}>
                + Upload New
              </s-button>
            </div>
          )}
        </div>

        {/* ══ TAB 1: Manual Upload ══ */}
        {mainTab === 1 && (
          <s-stack gap="base">

            {/* Upload error */}
            {uploadError && <s-banner tone="critical">{uploadError}</s-banner>}

            {/* ── Session check spinner (shown briefly while restoring active session) ── */}
            {initializing && phase === 'idle' && (
              <s-box padding="large" border="base" borderRadius="base" background="subdued">
                <s-stack gap="small" alignItems="center">
                  <div className="mu-spinner" />
                  <s-text tone="subdued" size="small">Checking for active sessions…</s-text>
                </s-stack>
              </s-box>
            )}

            {/* ── Drop zone (idle only, shown after session check completes) ── */}
            {phase === 'idle' && !initializing && (
              <>
                <div
                  className={`mu-drop-zone-container${isDragOver ? ' drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, true)}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPTED_LABEL}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <s-stack gap="small" alignItems="center">
                    <span className="drop-zone-icon">🖼️</span>
                    <s-text type="strong">{isDragOver ? 'Drop images here' : 'Add files'}</s-text>
                    <s-text tone="subdued" size="small">
                      or drop files to upload — {ACCEPTED_LABEL} · Max 20 MB · Up to 20 files
                    </s-text>
                  </s-stack>
                </div>

                {/* File-level validation errors */}
                {errors.length > 0 && (
                  <s-banner tone="warning">
                    <s-stack gap="small">
                      <s-text type="strong">Some files were skipped:</s-text>
                      {errors.map((err, i) => <s-text key={i} size="small">{err}</s-text>)}
                    </s-stack>
                  </s-banner>
                )}

                {/* Staged files table (Waiting) + upload controls */}
                {files.length > 0 && (
                  <s-box border="base" borderRadius="base" padding="none">
                    <s-stack gap="none">

                      {/* ── Toolbar header ── */}
                      <s-box padding="base" background="subdued" borderRadius="base base none none">
                        <s-stack direction="inline" justifyContent="space-between" alignItems="center">

                          {/* Left: file summary */}
                          <s-stack direction="inline" gap="base" alignItems="center">
                            <s-stack gap="extra-tight">
                              <s-stack direction="inline" gap="small" alignItems="center">
                                <s-text type="strong">Ready to upload</s-text>
                                <s-badge tone="info" size="small">{files.length} {files.length !== 1 ? 'files' : 'file'}</s-badge>
                                <s-badge size="small">{formatBytesAsSize(totalSelectedSize)}</s-badge>
                              </s-stack>
                              <s-text tone="subdued" size="small">
                                Choose a compression level, then upload to start optimizing.
                              </s-text>
                            </s-stack>
                          </s-stack>

                          {/* Right: controls */}
                          <s-stack direction="inline" gap="small" alignItems="center">
                            <OptimizationTypeSelector
                              optimizationType={uploadOptType}
                              onChange={setUploadOptType}
                            />
                            <s-button variant="secondary" icon="delete" onClick={clearAll}>
                              Clear all
                            </s-button>
                            <s-button variant="primary" icon="upload" onClick={handleUpload}>
                              Upload &amp; Optimize {files.length} {files.length !== 1 ? 'Images' : 'Image'}
                            </s-button>
                          </s-stack>
                        </s-stack>
                      </s-box>

                      {/* ── Column headers ── */}
                      <s-grid gridTemplateColumns={STAGED_FILE_COLS} gap="base" alignItems="center" paddingBlock="small" paddingInline="base" borderBottom="base">
                        <span />
                        <s-text size="small" type="strong">File</s-text>
                        <s-text size="small" type="strong">Size</s-text>
                        <s-text size="small" type="strong">Compressed</s-text>
                        <s-text size="small" type="strong">Saving</s-text>
                        <s-text size="small" type="strong">Status</s-text>
                        <s-text size="small" type="strong">Actions</s-text>
                      </s-grid>

                      {/* ── File rows ── */}
                      {files.map((f, idx) => (
                        <s-box key={`${f.name}-${idx}`} borderBottom={idx < files.length - 1 ? 'base' : undefined}>
                          <s-grid gridTemplateColumns={STAGED_FILE_COLS} gap="base" alignItems="center" paddingBlock="extra-small" paddingInline="base">
                            <StagedThumb file={f} />

                            <div style={{ minWidth: 0 }}>
                              <s-text size="small" type="strong" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {f.name}
                              </s-text>
                            </div>

                            <s-text size="small" tone="subdued">{formatBytesAsSize(f.size)}</s-text>
                            <s-text size="small" tone="subdued">—</s-text>
                            <s-text size="small" tone="subdued">—</s-text>
                            <s-badge size="small" tone="attention">Waiting</s-badge>

                            <s-button
                              size="small"
                              variant="tertiary"
                              icon="delete"
                              tone="critical"
                              onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                            >
                              Remove
                            </s-button>
                          </s-grid>
                        </s-box>
                      ))}

                    </s-stack>
                  </s-box>
                )}
              </>
            )}

            {/* ── Uploading spinner ── */}
            {phase === 'uploading' && (
              <s-box padding="base" border="base" borderRadius="base" background="subdued">
                <s-stack gap="base" alignItems="center">
                  <div className="mu-spinner" />
                  <s-text type="strong">Uploading images…</s-text>
                  <s-text tone="subdued">Optimization will start automatically.</s-text>
                </s-stack>
              </s-box>
            )}

            {/* ── Live session stats ── */}
            {phase !== 'idle' && phase !== 'uploading' && sessionData && (
              <UploadStatsRow sessionData={sessionData} />
            )}

            {/* ── Live progress card ── */}
            {(phase === 'optimizing' || phase === 'done') && (
              <div ref={progressRef}>
                <s-box padding="base" border="base" borderRadius="base">
                  <s-stack gap="base">

                  {/* Header */}
                  <s-stack direction="inline" justifyContent="space-between" alignItems="center">
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-heading size="small">Optimization Progress</s-heading>
                      {sessionData?.status === 'aborted'
                        ? <s-badge tone="critical">Aborted</s-badge>
                        : phase === 'done'
                          ? <s-badge tone="success">Completed</s-badge>
                          : <s-badge tone="warning">Running</s-badge>
                      }
                    </s-stack>
                    <s-stack direction="inline" gap="small" alignItems="center">
                      {/* Download All — shown as soon as there are done files, even while still running */}
                      {doneFiles.length > 1 && (
                        <s-button
                          variant="secondary"
                          size="small"
                          icon="download"
                          disabled={downloading.zip}
                          onClick={handleDownloadZip}
                        >
                          {downloading.zip ? 'Preparing ZIP…' : `Download All (${doneFiles.length})`}
                        </s-button>
                      )}
                      {/* Stop button */}
                      {phase === 'optimizing' && sessionData?.status !== 'aborted' && (
                        aborting
                          ? <s-text tone="critical" type="strong">Aborting…</s-text>
                          : (
                            <s-button
                              tone="critical"
                              size="small"
                              onClick={handleAbort}
                            >
                              Stop
                            </s-button>
                          )
                      )}
                    </s-stack>
                  </s-stack>

                  {/* Progress bar */}
                  <s-stack gap="small">
                    <s-stack direction="inline" justifyContent="space-between">
                      <s-text size="small">Progress: {liveProgressPct}%</s-text>
                      {sessionData?.status === 'aborted' && (
                        <s-text tone="critical" size="small" type="strong">Aborted (Partial)</s-text>
                      )}
                    </s-stack>
                    <div className="progress-bar-container">
                      <div
                        className={`progress-bar-fill ${phase === 'done' ? 'progress-completed' : 'progress-in-progress'}`}
                        style={{ width: `${liveProgressPct}%` }}
                      />
                    </div>
                  </s-stack>

                  {/* Counts */}
                  <s-stack direction="inline" gap="large">
                    {[
                      { label: 'Total',     value: liveTotal,  tone: undefined   },
                      { label: 'Done',      value: liveDone,   tone: 'success'   },
                      { label: 'Failed',    value: liveFailed, tone: 'critical'  },
                    ].map(({ label, value, tone }) => (
                      <s-stack key={label} gap="none">
                        <s-text size="small" tone="subdued">{label}</s-text>
                        <s-heading size="small" tone={tone}>{value}</s-heading>
                      </s-stack>
                    ))}
                  </s-stack>

                  {/* Savings summary (done only) */}
                  {phase === 'done' && totalSavedKb > 0 && (
                    <s-banner tone="success">
                      <s-text>
                        Saved {formatBytesAsSize(totalSavedKb * 1024)} across {doneFiles.length} image{doneFiles.length !== 1 ? 's' : ''}.
                      </s-text>
                    </s-banner>
                  )}

                  {/* File table */}
                  {fileRows.length > 0 && (
                    <>
                      <div className="batch-table-header" style={{ gridTemplateColumns: FILE_COLS }}>
                        <span />
                        <s-text size="small" type="strong">File</s-text>
                        <s-text size="small" type="strong">Original</s-text>
                        <s-text size="small" type="strong">Compressed</s-text>
                        <s-text size="small" type="strong">Saving</s-text>
                        <s-text size="small" type="strong">Status</s-text>
                        <s-text size="small" type="strong" style={{ textAlign: 'right', display: 'block' }}>Actions</s-text>
                      </div>
                      {fileRows.map(file => (
                        <BatchFileRow
                          key={file.id}
                          file={file}
                          sessionId={sessionData?.sessionId || 'live'}
                          onDownload={handleLiveDownload}
                          onCompare={handleLiveCompare}
                          downloading={{ file: downloading.file, zip: false }}
                          fetchRef={liveFetchRef}
                        />
                      ))}
                    </>
                  )}

                </s-stack>
              </s-box>
            </div>)}

            {/* Recent uploads (always visible) */}
            <RecentUploads
              recentsKey={recentsKey}
              onCompare={setCompareData}
              currentSessionId={phase !== 'idle' ? sessionData?.sessionId : null}
            />
          </s-stack>
        )}

        {/* ══ TAB 0: Product Images ══ */}
        {mainTab === 0 && (
          <s-stack gap="none">
            <div className="tab-bar-container sub-tab-bar">
              <button
                className={`sub-tab-button ${subTab === 0 ? 'sub-tab-button-active' : ''}`}
                onClick={() => setSubTab(0)}
              >
                Unoptimized
                {!statsLoading && stats?.products && (
                  <s-badge size="small" tone={unoptimizedTabCount > 0 ? 'warning' : 'neutral'}>
                    {unoptimizedTabCount}
                  </s-badge>
                )}
              </button>
              <button
                className={`sub-tab-button ${subTab === 1 ? 'sub-tab-button-active' : ''}`}
                onClick={() => setSubTab(1)}
              >
                Optimized
                {!statsLoading && stats?.products && (
                  <s-badge size="small" tone={optimizedTabCount > 0 ? 'success' : 'neutral'}>
                    {optimizedTabCount}
                  </s-badge>
                )}
              </button>
            </div>
            <s-box padding="base" border="base" borderStyle="none solid solid solid">
              {subTab === 0 ? (
                <ProductSelection onJobStarted={fetchStats} hideCredits />
              ) : (
                <OptimizedImagesManagement onJobStarted={fetchStats} hideCredits onGoToUnoptimized={() => setSubTab(0)} />
              )}
            </s-box>
          </s-stack>
        )}

      </s-stack>

      <CompareModal data={compareData} onClose={() => setCompareData(null)} />
    </s-section>
  );
}

export default ImageCrushPage;
